import React, { useEffect, useRef, useState } from 'react'

import { observer } from 'mobx-react'
import { useNavigate } from 'react-router-dom'

import cn from 'classnames'

import { ClickAwayListener, Tooltip } from '@mui/material'
import { makeStyles, styled } from '@mui/material/styles'

import { notifications_link } from 'utils'
import { getDate, getTime } from 'utils/functions'

import { INotification } from 'models/notification'

import { useStore } from 'store'

import { ReactComponent as Close } from 'sources/images/close.svg'
import defaultUserPhoto from 'sources/images/defaultUser.svg'
import { ReactComponent as Bell } from 'sources/images/notification_bell.svg'

import styles from './styles.module.scss'

const PREFIX = 'NotificationBell'

const classes = {
  customTooltip: `${PREFIX}-customTooltip`,
}

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.customTooltip}`]: {
    maxWidth: '485px',
    width: '98vw',
  },
}))

interface IProps {
  bellWhite?: boolean
}

//Resize
export const NotificationBell: React.FC<IProps> = observer(({ bellWhite }) => {
  const navigate = useNavigate()
  const { notification, publication, profile, mediaQuery } = useStore()

  const [open, setOpen] = useState(false)
  const [lastThreeUnreadNotifications, setLastThreeUnreadNotifications] =
    useState<INotification[]>([])
  const openRef = useRef(open)
  openRef.current = open

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleScroll = () => {
    if (openRef.current) {
      setOpen(false)
    }
  }

  const handleTooltipClose = () => {
    setOpen(false)
    setLastThreeUnreadNotifications([])
  }

  const handleTooltipOpen = async () => {
    if (!open) {
      if (notification.notificationsCountUnread > 0) {
        const response = await notification.getUnreadNotifications()
        setLastThreeUnreadNotifications(response)
      }
    }
    setOpen(prev => !prev)
  }

  const redirectToNotificationPage = async () => {
    setOpen(false)
    navigate(notifications_link())
  }

  const notificationsList = () => {
    return (
      <Root className={styles.notification}>
        <div className={styles.notificationHeader}>
          <div className={styles.titleBlock}>
            Notifications
            <div className={styles.newNotificationQuantity}>
              {notification.notificationsCountUnread ||
                lastThreeUnreadNotifications.length}
            </div>
          </div>
          <button className={styles.onCloseBtn} onClick={handleTooltipClose}>
            <Close />
          </button>
        </div>
        <div className={styles.notificationBody}>
          {notification.notificationsCountUnread > 0 ||
          lastThreeUnreadNotifications.length > 0 ? (
            lastThreeUnreadNotifications.map(item => (
              <div className={styles.notificationItem} key={item.Id}>
                <div className={styles.userInfoBox}>
                  <div className={styles.userAvatarBox}>
                    <img
                      className={styles.userAvatar}
                      src={
                        item.FromUserInfo.ImageLink
                          ? item.FromUserInfo.ImageLink
                          : defaultUserPhoto
                      }
                      alt="avatar"
                    />
                    {item.IsReadClient ? null : (
                      <div className={styles.circle} />
                    )}
                  </div>
                  <div className={styles.userInfo}>
                    <div className={styles.userNameBox}>
                      <div className={styles.userName}>
                        {item.FromUserInfo.FirstName}{' '}
                        {item.FromUserInfo.LastName}
                      </div>
                      <div className={styles.userNickName}>
                        @{item.FromUserInfo.NickName}
                      </div>
                    </div>
                    <div className={styles.userAction}>
                      {item.NotificationText}
                    </div>
                  </div>
                </div>
                <div className={styles.sendData}>
                  <div className={styles.date}>{getDate(item.CreatedAt)}</div>
                  <div className={styles.time}>{getTime(item.CreatedAt)}</div>
                </div>
              </div>
            ))
          ) : (
            <div className={styles.noNotifications}>
              You don't have unread notifications
            </div>
          )}
        </div>
        <div className={styles.notificationFooter}>
          <div onClick={redirectToNotificationPage}>View all</div>
        </div>
      </Root>
    )
  }

  return (
    // <div className={styles.container} onClick={event => event.stopPropagation()}>
    <div className={styles.container}>
      <ClickAwayListener onClickAway={handleTooltipClose}>
        <Tooltip
          classes={{
            popper: styles.popper,
            tooltip: classes.customTooltip,
            popperArrow: styles.popperArrow,
          }}
          onClose={handleTooltipClose}
          open={open}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          placement="top"
          title={notificationsList()}
          arrow
        >
          <button
            type="button"
            onClick={handleTooltipOpen}
            className={styles.btnBell}
          >
            {notification.notificationsCountUnread > 0 ? (
              <div className={styles.quantity}>
                {notification.notificationsCountUnread > 999 ? (
                  <>
                    <span
                      style={{
                        fontSize: mediaQuery.minWidth_2200 ? '13px' : '11px',
                      }}
                    >
                      +
                    </span>
                    999
                  </>
                ) : (
                  notification.notificationsCountUnread
                )}
              </div>
            ) : null}
            <Bell
              className={cn({
                [styles.bell]: true,
                [styles.bellWhite]: bellWhite,
                [styles.bellNew]: notification.notificationsCountUnread > 0,
              })}
            />
          </button>
        </Tooltip>
      </ClickAwayListener>
    </div>
  );
})

NotificationBell.displayName = 'NotificationBell'
